import { useTranslation } from 'react-i18next'
import { useRole } from '@structural-climbing/auth'
import { Button, Spinner, Tab, Tabs, useTabParam } from '@structural-climbing/ui'
import { useGymContext } from '../contexts/GymContext'
import { CatalogueTab } from '../features/products/CatalogueTab'
import { SalesTab } from '../features/products/SalesTab'
import { SellTab } from '../features/products/SellTab'
import { useProducts } from '../features/products/useProducts'
import { PageHeader } from '../components/PageHeader'

const OWNER_TABS = ['sell', 'catalogue', 'sales'] as const
const STAFF_TABS = ['sell'] as const
type ProductsTab = (typeof OWNER_TABS)[number]

export function ProductsPage() {
  const { t } = useTranslation()
  const { productsEnabled } = useGymContext()
  // The catalogue and the profit figures are the owner's. Staff holding the
  // Products permission sell, and the Worker enforces the same split.
  const isOwner = useRole() === 'gym_owner'
  const tabs: readonly ProductsTab[] = isOwner ? OWNER_TABS : STAFF_TABS
  // In the URL, so a refresh stays on the tab being used.
  const [tab, setTab] = useTabParam<ProductsTab>('tab', tabs, 'sell')

  if (!productsEnabled) {
    return (
      <div className="p-4 sm:p-8">
        <h1 className="mb-1 text-2xl font-bold">{t('products.title')}</h1>
        <p className="text-foreground/60">{t('products.notEnabled')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader title={t('products.title')} subtitle={t('products.subtitle')} />
      <ProductsContent isOwner={isOwner} tab={tab} setTab={setTab} />
    </div>
  )
}

function ProductsContent({
  isOwner,
  tab,
  setTab,
}: {
  isOwner: boolean
  tab: ProductsTab
  setTab: (tab: ProductsTab) => void
}) {
  const { t } = useTranslation()
  const { categories, products, productsById, currency, loading, error, reload } = useProducts()

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="sm" color="primary" />
      </div>
    )
  }
  if (error) return <p className="text-sm text-danger">{t('products.errorLoad')}</p>

  const sell = (
    <SellTab
      categories={categories}
      products={products}
      productsById={productsById}
      currency={currency}
      onStockChanged={() => void reload()}
      emptyState={
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="text-sm text-foreground/50">
            {isOwner ? t('products.emptyCatalogueOwner') : t('products.emptyCatalogueStaff')}
          </p>
          {isOwner && (
            <Button color="primary" onPress={() => setTab('catalogue')}>
              {t('products.goToCatalogue')}
            </Button>
          )}
        </div>
      }
    />
  )

  // One tab is not a choice: staff get the sell screen with no strip above it.
  if (!isOwner) return sell

  return (
    <>
      <Tabs
        aria-label={t('products.title')}
        selectedKey={tab}
        onSelectionChange={(key) => setTab(key as ProductsTab)}
        classNames={{
          base: 'max-w-full',
          tabList: 'max-w-full flex-nowrap overflow-x-auto scrollbar-hide',
        }}
      >
        <Tab key="sell" title={t('products.tabSell')} />
        <Tab key="catalogue" title={t('products.tabCatalogue')} />
        <Tab key="sales" title={t('products.tabSales')} />
      </Tabs>

      {tab === 'sell' && sell}
      {tab === 'catalogue' && (
        <CatalogueTab
          categories={categories}
          products={products}
          currency={currency}
          onRefresh={() => void reload()}
        />
      )}
      {tab === 'sales' && <SalesTab />}
    </>
  )
}
